/*!
 * Three.js animation utilities for Sense Shopify theme
 */

// Use existing global variables instead of redeclaring
// Only create references if not already defined
let _THREE = window.THREE;
let _GLTFLoader = window.GLTFLoader;

// Function to check if Three.js and GLTFLoader are available
function checkDependencies() {
  return new Promise((resolve, reject) => {
    // Check if already available (loaded via module)
    if (window.THREE && window.GLTFLoader) {
      _THREE = window.THREE;
      _GLTFLoader = window.GLTFLoader;
      console.log("Dependencies found in window object");
      resolve();
      return;
    }
    
    // If not available, wait for them to be loaded
    console.log("THREE or GLTFLoader not available yet, waiting...");
    
    // Wait for modules to load
    let attempts = 0;
    const maxAttempts = 20;
    
    const checkAgain = () => {
      if (window.THREE && window.GLTFLoader) {
        _THREE = window.THREE;
        _GLTFLoader = window.GLTFLoader;
        console.log("Dependencies found after waiting");
        resolve();
        return;
      }
      
      attempts++;
      if (attempts >= maxAttempts) {
        console.error("Failed to load Three.js dependencies after", maxAttempts, "attempts");
        reject(new Error("Failed to load Three.js dependencies after multiple attempts"));
        return;
      }
      
      console.log(`Waiting for dependencies (attempt ${attempts}/${maxAttempts})...`);
      setTimeout(checkAgain, 300);
    };
    
    checkAgain();
  });
}

// Base class for Three.js animations
class BaseAnimation {
  constructor(container, options = {}) {
    this.container = container;
    this.options = Object.assign({
      backgroundColor: 0xf5f5f5,
      cameraPosition: { x: 0, y: 0, z: 5 },
      autoRotate: true
    }, options);
    
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.animationFrame = null;
    this.clock = null;
    this.isInitialized = false;
    
    // Add debug element if present
    this.debugElement = this.container?.parentElement?.querySelector('.debug-info');
    if (this.debugElement) {
      this.debugElement.style.display = 'block';
      this.showDebug('Initializing animation...');
    }
  }
  
  showDebug(message) {
    if (this.debugElement) {
      this.debugElement.textContent = message;
      console.log(message);
    } else {
      console.log(message);
    }
  }

  async init() {
    if (this.isInitialized) return;
    
    try {
      // Make sure Three.js and GLTFLoader are loaded
      await checkDependencies();
      
      this.showDebug('Dependencies loaded, setting up scene...');
      
      // Create scene, camera, and renderer
      this.scene = new _THREE.Scene();
      if (this.options.backgroundColor) {
        this.scene.background = new _THREE.Color(this.options.backgroundColor);
      }
      
      const { width, height } = this.container.getBoundingClientRect();
      
      this.camera = new _THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      this.camera.position.set(
        this.options.cameraPosition.x,
        this.options.cameraPosition.y,
        this.options.cameraPosition.z
      );
      
      this.renderer = new _THREE.WebGLRenderer({ 
        antialias: true,
        alpha: true 
      });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.container.appendChild(this.renderer.domElement);
      
      this.clock = new _THREE.Clock();
      
      // Add window resize handler
      window.addEventListener('resize', this.onResize.bind(this));
      
      // Set up scene (to be implemented by child classes)
      await this.setupScene();
      
      this.isInitialized = true;
      this.animate();
    } catch (error) {
      console.error('Failed to initialize animation:', error);
      this.showDebug('Failed to initialize: ' + error.message);
      this.fallback();
    }
  }
  
  setupScene() {
    // To be implemented by child classes
    console.warn('setupScene method not implemented');
  }
  
  animate() {
    this.animationFrame = requestAnimationFrame(this.animate.bind(this));
    
    // To be implemented/extended by child classes
    if (this.options.autoRotate && this.scene) {
      this.scene.rotation.y += 0.005;
    }
    
    this.renderer.render(this.scene, this.camera);
  }
  
  onResize() {
    if (!this.camera || !this.renderer) return;
    
    const { width, height } = this.container.getBoundingClientRect();
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }
  
  destroy() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    window.removeEventListener('resize', this.onResize.bind(this));
    
    if (this.renderer && this.container.contains(this.renderer.domElement)) {
      this.container.removeChild(this.renderer.domElement);
    }
    
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.clock = null;
    this.isInitialized = false;
  }
  
  fallback() {
    // Simple fallback if WebGL not supported
    this.container.classList.add('threejs-fallback');
  }
}

// Testimonial animation with floating cards
class TestimonialAnimation extends BaseAnimation {
  constructor(container, testimonials, options = {}) {
    super(container, options);
    this.testimonials = testimonials || [];
    this.cards = [];
    this.cardGroup = null;
    this.currentIndex = 0;
    
    // Get rotation settings from section settings
    const section = container.closest('.animated-testimonials');
    this.autoRotate = section.dataset.autoRotate !== 'false';
    
    // Convert the rotation speed (1-10) to a suitable value (0.05-0.5)
    const rotationSpeedSetting = parseInt(section.dataset.rotationSpeed || '5', 10);
    this.rotationSpeed = 0.05 + ((rotationSpeedSetting - 1) / 9) * 0.45;
    
    this.transitionInProgress = false;
  }
  
  setupScene() {
    // Add lighting
    const ambientLight = new _THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);
    
    const directionalLight = new _THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    this.scene.add(directionalLight);
    
    // Create testimonial cards
    this.cardGroup = new _THREE.Group();
    this.scene.add(this.cardGroup);
    
    // Create cards for each testimonial
    this.testimonials.forEach((testimonial, index) => {
      const card = this.createCard(testimonial, index);
      this.cards.push(card);
      this.cardGroup.add(card);
    });

    // Setup UI controls events
    this.setupNavigation();
  }
  
  setupNavigation() {
    // Find navigation elements
    const prevButton = this.container.closest('.animated-testimonials').querySelector('.testimonial-nav-prev');
    const nextButton = this.container.closest('.animated-testimonials').querySelector('.testimonial-nav-next');
    const indicators = this.container.closest('.animated-testimonials').querySelectorAll('.testimonial-indicator');
    
    // Setup navigation buttons
    if (prevButton) {
      prevButton.addEventListener('click', () => this.navigateToPrev());
    }
    
    if (nextButton) {
      nextButton.addEventListener('click', () => this.navigateToNext());
    }
    
    // Setup indicators
    if (indicators.length) {
      indicators.forEach((indicator, index) => {
        indicator.addEventListener('click', () => {
          if (this.currentIndex === index || this.transitionInProgress) return;
          this.navigateToIndex(index);
        });
      });
    }
  }
  
  createCard(testimonial, index) {
    // Create a simple card geometry
    const cardGeometry = new _THREE.BoxGeometry(2, 1, 0.05);
    
    // Create material with subtle shine
    const cardMaterial = new _THREE.MeshPhongMaterial({
      color: 0xffffff,
      specular: 0x111111,
      shininess: 30
    });
    
    const card = new _THREE.Mesh(cardGeometry, cardMaterial);
    
    // Position cards in a circle
    const angle = (index / this.testimonials.length) * Math.PI * 2;
    const radius = 3;
    card.position.x = Math.sin(angle) * radius;
    card.position.z = Math.cos(angle) * radius;
    card.position.y = Math.sin(angle * 2) * 0.5; // Slight up/down variation
    
    // Rotate card to face center
    card.lookAt(0, 0, 0);
    
    // Store original position for animation
    card.userData = {
      originalPosition: card.position.clone(),
      originalRotation: card.rotation.clone(),
      index: index,
      angle: angle
    };
    
    return card;
  }
  
  animate() {
    this.animationFrame = requestAnimationFrame(this.animate.bind(this));
    
    const time = this.clock.getElapsedTime();
    
    // Animate each card
    this.cards.forEach(card => {
      const { index, angle } = card.userData;
      
      // Wave-like motion
      card.position.y = card.userData.originalPosition.y + Math.sin(time + index) * 0.2;
      
      // Slight rotation animation
      card.rotation.z = card.userData.originalRotation.z + Math.sin(time * 0.5 + index) * 0.1;
    });
    
    // Slowly rotate the entire card group if auto-rotate is enabled
    if (this.cardGroup && this.autoRotate && !this.transitionInProgress) {
      this.cardGroup.rotation.y += this.rotationSpeed * 0.01;
    }
    
    this.renderer.render(this.scene, this.camera);
  }

  navigateToNext() {
    if (this.transitionInProgress || this.testimonials.length <= 1) return;
    
    this.transitionInProgress = true;
    const targetRotation = this.cardGroup.rotation.y - (Math.PI * 2 / this.testimonials.length);
    
    // Animate to the next testimonial
    this.animateRotation(targetRotation, () => {
      this.currentIndex = (this.currentIndex + 1) % this.testimonials.length;
      this.updateActiveTestimonial();
      this.transitionInProgress = false;
    });
  }
  
  navigateToPrev() {
    if (this.transitionInProgress || this.testimonials.length <= 1) return;
    
    this.transitionInProgress = true;
    const targetRotation = this.cardGroup.rotation.y + (Math.PI * 2 / this.testimonials.length);
    
    // Animate to the previous testimonial
    this.animateRotation(targetRotation, () => {
      this.currentIndex = (this.currentIndex - 1 + this.testimonials.length) % this.testimonials.length;
      this.updateActiveTestimonial();
      this.transitionInProgress = false;
    });
  }
  
  animateRotation(targetRotation, callback) {
    const startRotation = this.cardGroup.rotation.y;
    const duration = 0.75; // seconds
    const startTime = this.clock.getElapsedTime();
    
    const animate = () => {
      const currentTime = this.clock.getElapsedTime();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easing function for smoother transition
      const easeProgress = 1 - Math.pow(1 - progress, 3); // cubic ease out
      
      this.cardGroup.rotation.y = startRotation + (targetRotation - startRotation) * easeProgress;
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        if (callback) callback();
      }
    };
    
    animate();
  }
  
  updateActiveTestimonial() {
    // Update visible testimonial in DOM
    const testimonialSlides = this.container.closest('.animated-testimonials').querySelectorAll('.testimonial-slide');
    const indicators = this.container.closest('.animated-testimonials').querySelectorAll('.testimonial-indicator');
    
    testimonialSlides.forEach((slide, index) => {
      if (index === this.currentIndex) {
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }
    });
    
    // Update indicators
    indicators.forEach((indicator, index) => {
      if (index === this.currentIndex) {
        indicator.classList.add('active');
      } else {
        indicator.classList.remove('active');
      }
    });
  }
  
  navigateToIndex(index) {
    if (this.transitionInProgress || index === this.currentIndex) return;
    
    this.transitionInProgress = true;
    
    // Calculate shortest direction (clockwise or counterclockwise)
    const totalItems = this.testimonials.length;
    const currentPosition = this.currentIndex;
    const targetPosition = index;
    
    let clockwiseDistance = (targetPosition - currentPosition + totalItems) % totalItems;
    let counterclockwiseDistance = (currentPosition - targetPosition + totalItems) % totalItems;
    
    let direction = 1; // 1 for clockwise (next), -1 for counterclockwise (prev)
    
    // Choose shortest path
    if (counterclockwiseDistance < clockwiseDistance) {
      direction = -1;
    }
    
    // Calculate target rotation
    const rotationPerItem = Math.PI * 2 / totalItems;
    const targetRotation = this.cardGroup.rotation.y + (direction * rotationPerItem * clockwiseDistance);
    
    // Animate to the target position
    this.animateRotation(targetRotation, () => {
      this.currentIndex = index;
      this.updateActiveTestimonial();
      this.transitionInProgress = false;
    });
  }
}

// Featured products animation with floating product models
class FeaturedAnimation extends BaseAnimation {
  constructor(container, products, options = {}) {
    super(container, options);
    this.products = products || [];
    this.models = [];
    this.particleSystem = null;
  }
  
  setupScene() {
    // Add lighting for dramatic effect
    const ambientLight = new _THREE.AmbientLight(0xffffff, 0.4);
    this.scene.add(ambientLight);
    
    const keyLight = new _THREE.DirectionalLight(0xffffff, 0.8);
    keyLight.position.set(1, 1, 2);
    this.scene.add(keyLight);
    
    const fillLight = new _THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-1, 0.5, 1);
    this.scene.add(fillLight);
    
    // Create product models
    this.products.forEach((product, index) => {
      const model = this.createModel(product, index);
      this.models.push(model);
      this.scene.add(model);
    });
    
    // Add particle background effect
    this.addParticles();
  }
  
  createModel(product, index) {
    // Create a simple product representation using basic shapes
    const group = new _THREE.Group();
    
    // Base shape - can be customized per product type
    const baseGeometry = new _THREE.BoxGeometry(1, 1, 1);
    const baseMaterial = new _THREE.MeshPhongMaterial({
      color: this.getRandomColor(),
      specular: 0x333333,
      shininess: 50
    });
    
    const base = new _THREE.Mesh(baseGeometry, baseMaterial);
    group.add(base);
    
    // Add detail shapes to make it more interesting
    const detailGeometry = new _THREE.SphereGeometry(0.3, 16, 16);
    const detailMaterial = new _THREE.MeshPhongMaterial({
      color: this.getRandomColor(),
      specular: 0xffffff,
      shininess: 100
    });
    
    const detail = new _THREE.Mesh(detailGeometry, detailMaterial);
    detail.position.y = 0.7;
    group.add(detail);
    
    // Position in a circle
    const angle = (index / this.products.length) * Math.PI * 2;
    const radius = 3.5;
    group.position.x = Math.sin(angle) * radius;
    group.position.z = Math.cos(angle) * radius;
    
    // Store animation data
    group.userData = {
      originalPosition: group.position.clone(),
      angle: angle,
      index: index,
      speed: 0.5 + Math.random() * 0.5
    };
    
    return group;
  }
  
  addParticles() {
    const particleCount = 1000;
    const particles = new _THREE.BufferGeometry();
    
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    
    for (let i = 0; i < particleCount; i++) {
      // Random position in a sphere
      const radius = 10 + Math.random() * 5;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
      
      // Soft colors
      colors[i * 3] = 0.7 + Math.random() * 0.3;
      colors[i * 3 + 1] = 0.7 + Math.random() * 0.3; 
      colors[i * 3 + 2] = 0.7 + Math.random() * 0.3;
    }
    
    particles.setAttribute('position', new _THREE.BufferAttribute(positions, 3));
    particles.setAttribute('color', new _THREE.BufferAttribute(colors, 3));
    
    const particleMaterial = new _THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0.5
    });
    
    this.particleSystem = new _THREE.Points(particles, particleMaterial);
    this.scene.add(this.particleSystem);
  }
  
  animate() {
    this.animationFrame = requestAnimationFrame(this.animate.bind(this));
    
    const time = this.clock.getElapsedTime();
    
    // Animate product models
    this.models.forEach(model => {
      const { index, angle, speed } = model.userData;
      
      // Floating motion
      model.position.y = Math.sin(time * speed) * 0.5;
      
      // Rotation animation
      model.rotation.y = time * speed * 0.5;
      model.rotation.z = Math.sin(time * 0.3) * 0.1;
    });
    
    // Animate particle system
    if (this.particleSystem) {
      this.particleSystem.rotation.y = time * 0.05;
      this.particleSystem.rotation.x = time * 0.03;
    }
    
    this.renderer.render(this.scene, this.camera);
  }
  
  getRandomColor() {
    return new _THREE.Color(
      0.5 + Math.random() * 0.5,
      0.5 + Math.random() * 0.5,
      0.5 + Math.random() * 0.5
    );
  }
}

// 3D Model Viewer for GLB files
class ModelViewer extends BaseAnimation {
  constructor(container, modelUrl, options = {}) {
    super(container, options);
    this.modelUrl = modelUrl || '';
    this.model = null;
    this.isDragging = false;
    this.previousMousePosition = { x: 0, y: 0 };
    this.rotationSpeed = options.rotationSpeed || 0.005;
    this.autoRotate = options.autoRotate !== false;
  }
  
  async setupScene() {
    // Add lighting
    const ambientLight = new _THREE.AmbientLight(0xffffff, 0.7);
    this.scene.add(ambientLight);
    
    const directionalLight = new _THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(0, 10, 10);
    this.scene.add(directionalLight);
    
    const backLight = new _THREE.DirectionalLight(0xffffff, 0.5);
    backLight.position.set(0, 5, -10);
    this.scene.add(backLight);
    
    // Load the GLB model
    try {
      this.showDebug('Loading model from: ' + this.modelUrl);
      await this.loadModel();
      
      // Add interaction controls
      this.setupControls();
      this.showDebug('Model loaded successfully');
    } catch (error) {
      console.error('Error loading model:', error);
      this.showDebug('Error loading model: ' + error.message);
    }
  }
  
  async loadModel() {
    if (!this.modelUrl) {
      this.showDebug('No model URL provided');
      return Promise.reject(new Error('No model URL provided'));
    }
    
    // Use GLTFLoader to load the model
    return new Promise((resolve, reject) => {
      try {
        // Create a new loader instance
        if (!_GLTFLoader) {
          this.showDebug('GLTFLoader not available');
          reject(new Error('GLTFLoader not available'));
          return;
        }
        
        const loader = new _GLTFLoader();
        
        // Enable CORS for the loader
        loader.crossOrigin = 'anonymous';
        
        this.showDebug('Starting to load model: ' + this.modelUrl);
        
        loader.load(
          this.modelUrl,
          (gltf) => {
            this.showDebug('Model loaded successfully');
            this.model = gltf.scene;
            
            // Center and scale the model
            const box = new _THREE.Box3().setFromObject(this.model);
            const size = box.getSize(new _THREE.Vector3()).length();
            const center = box.getCenter(new _THREE.Vector3());
            
            this.model.position.x = -center.x;
            this.model.position.y = -center.y;
            this.model.position.z = -center.z;
            
            // Scale model to appropriate size
            const scale = 2.5 / size;
            this.model.scale.set(scale, scale, scale);
            
            this.scene.add(this.model);
            resolve(this.model);
          },
          (xhr) => {
            // Loading progress
            const percent = (xhr.loaded / xhr.total) * 100;
            this.showDebug(`Loading model: ${Math.round(percent)}%`);
          },
          (error) => {
            this.showDebug('Error loading model: ' + error.message);
            console.error('Error loading model:', error);
            reject(error);
          }
        );
      } catch (error) {
        this.showDebug('Error initializing loader: ' + error.message);
        console.error('Error initializing loader:', error);
        reject(error);
      }
    });
  }
  
  setupControls() {
    const canvas = this.renderer.domElement;
    
    // Mouse controls
    canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
    
    // Touch controls for mobile
    canvas.addEventListener('touchstart', this.onTouchStart.bind(this));
    document.addEventListener('touchmove', this.onTouchMove.bind(this));
    document.addEventListener('touchend', this.onTouchEnd.bind(this));
    
    // Zoom controls
    canvas.addEventListener('wheel', this.onWheel.bind(this));
  }
  
  onMouseDown(event) {
    this.isDragging = true;
    this.previousMousePosition = {
      x: event.clientX,
      y: event.clientY
    };
    event.preventDefault();
  }
  
  onMouseMove(event) {
    if (!this.isDragging || !this.model) return;
    
    const deltaMove = {
      x: event.clientX - this.previousMousePosition.x,
      y: event.clientY - this.previousMousePosition.y
    };
    
    // Rotate model based on mouse movement
    this.model.rotation.y += deltaMove.x * 0.01;
    this.model.rotation.x += deltaMove.y * 0.01;
    
    this.previousMousePosition = {
      x: event.clientX,
      y: event.clientY
    };
  }
  
  onMouseUp() {
    this.isDragging = false;
  }
  
  onTouchStart(event) {
    if (event.touches.length === 1) {
      this.isDragging = true;
      this.previousMousePosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
      event.preventDefault();
    }
  }
  
  onTouchMove(event) {
    if (!this.isDragging || !this.model || event.touches.length !== 1) return;
    
    const deltaMove = {
      x: event.touches[0].clientX - this.previousMousePosition.x,
      y: event.touches[0].clientY - this.previousMousePosition.y
    };
    
    // Rotate model based on touch movement
    this.model.rotation.y += deltaMove.x * 0.01;
    this.model.rotation.x += deltaMove.y * 0.01;
    
    this.previousMousePosition = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY
    };
  }
  
  onTouchEnd() {
    this.isDragging = false;
  }
  
  onWheel(event) {
    if (!this.model) return;
    
    // Prevent default scrolling behavior
    event.preventDefault();
    
    // Adjust camera position based on wheel movement
    const zoomSpeed = 0.1;
    const delta = Math.sign(event.deltaY) * zoomSpeed;
    
    // Limit how close/far we can zoom
    const newZ = Math.max(2, Math.min(8, this.camera.position.z + delta));
    this.camera.position.z = newZ;
  }
  
  animate() {
    this.animationFrame = requestAnimationFrame(this.animate.bind(this));
    
    // Auto-rotate model if enabled and not being dragged
    if (this.model && this.autoRotate && !this.isDragging) {
      this.model.rotation.y += this.rotationSpeed;
    }
    
    this.renderer.render(this.scene, this.camera);
  }
}

// Initialize the specific interactive model for this page
function initInteractiveModel() {
  console.log("Initializing interactive model...");
  const containers = document.querySelectorAll('.threejs-model-viewer-canvas');
  
  containers.forEach(container => {
    const section = container.closest('.interactive-3d-model');
    if (!section) {
      console.log("No parent section found for container");
      return;
    }
    
    // Get model URL from section
    const modelUrl = section.dataset.modelUrl || '';
    console.log("Model URL:", modelUrl);
    
    const animation = new ModelViewer(container, modelUrl, {
      backgroundColor: section.dataset.backgroundColor || 0xf5f5f5,
      autoRotate: section.dataset.autoRotate !== 'false',
      rotationSpeed: parseFloat(section.dataset.rotationSpeed || 0.005)
    });
    
    // Initialize animation
    animation.init();
    
    // Store animation reference for cleanup
    container.animation = animation;
  });
}

// Initialize animations when DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  // Find animation containers
  const testimonialContainers = document.querySelectorAll('.animated-testimonials-canvas');
  const featuredContainers = document.querySelectorAll('.animated-featured-section-canvas');
  
  // Initialize testimonial animations
  testimonialContainers.forEach(container => {
    const section = container.closest('.animated-testimonials');
    if (!section) return;
    
    // Get testimonials data from section
    const testimonials = JSON.parse(section.dataset.testimonials || '[]');
    
    const animation = new TestimonialAnimation(container, testimonials, {
      backgroundColor: section.dataset.backgroundColor || 0xf5f5f5,
      autoRotate: section.dataset.autoRotate !== 'false'
    });
    
    // Initialize animation
    animation.init();
    
    // Store animation reference for cleanup
    container.animation = animation;
  });
  
  // Initialize featured section animations
  featuredContainers.forEach(container => {
    const section = container.closest('.animated-featured-section');
    if (!section) return;
    
    // Get products data from section
    const products = JSON.parse(section.dataset.products || '[]');
    
    const animation = new FeaturedAnimation(container, products, {
      backgroundColor: section.dataset.backgroundColor || 0xf3f3f3,
      autoRotate: section.dataset.autoRotate !== 'false'
    });
    
    // Initialize animation
    animation.init();
    
    // Store animation reference for cleanup
    container.animation = animation;
  });
  
  // Make the initialization function available globally
  window.initInteractiveModel = initInteractiveModel;
  
  // Try to initialize interactive models
  if (window.THREE && window.GLTFLoader) {
    console.log("Dependencies ready, initializing model...");
    setTimeout(initInteractiveModel, 500);
  } else {
    console.log("Dependencies not ready yet, will be initialized by ES module loader");
  }
});

// Cleanup animations when section is unloaded
document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target;
  
  // Find and cleanup testimonial animations
  const testimonialCanvas = section.querySelector('.animated-testimonials-canvas');
  if (testimonialCanvas && testimonialCanvas.animation) {
    testimonialCanvas.animation.destroy();
    testimonialCanvas.animation = null;
  }
  
  // Find and cleanup featured section animations
  const featuredCanvas = section.querySelector('.animated-featured-section-canvas');
  if (featuredCanvas && featuredCanvas.animation) {
    featuredCanvas.animation.destroy();
    featuredCanvas.animation = null;
  }
  
  // Find and cleanup model viewer animations
  const modelViewerCanvas = section.querySelector('.threejs-model-viewer-canvas');
  if (modelViewerCanvas && modelViewerCanvas.animation) {
    modelViewerCanvas.animation.destroy();
    modelViewerCanvas.animation = null;
  }
}); 